// src/components/layout/Layout.jsx
import React, { useState, useContext, useRef, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, Circle, Plus, MoreHorizontal, Wallet, ClipboardList, Home, Store, ShoppingBag, ArrowLeftRight, FileText, Sparkles, Settings, Percent, Inbox, Mail } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import MobileBottomNav from "./MobileBottomNavbar";
import SideDrawer from "./SideDrawer";
import { ThemeContext } from "../context/themeContext";
import { switchToPersonal, getPersonalToken } from "../../services/switchACcount";
import { showToast } from "../../utils/helpers";
import AddressPromptModal from "../dashboard/AddressPromptModal";

const DashboardTabContext = React.createContext();
export { DashboardTabContext };

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const { theme, toggleTheme } = useContext(ThemeContext);

  const [accountType, setAccountType] = useState('business');
  const [dashboardTab, setDashboardTab] = useState('feed');
  const [showAddressPrompt, setShowAddressPrompt] = useState(false);
  const [headerVisible, setHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const scrollTimeoutRef = useRef(null);

  // After login, ask sellers who haven't submitted a business address to add
  // one (unless they're inside the onboarding/setup flow, which collects it, or
  // they dismissed it with "Maybe later" this session).
  useEffect(() => {
    if (!currentUser) return;
    // Once an address is actually submitted, forget any earlier dismissal so
    // the prompt can show again if the address is later cleared.
    if (Number(currentUser.has_submitted_address) === 1) {
      sessionStorage.removeItem('paymeo_address_prompt_dismissed');
    }
    const hasAddress = !!(currentUser.company_address && currentUser.company_address.trim());
    const submitted = Number(currentUser.has_submitted_address) === 1;
    const needsAddress = !hasAddress || !submitted;
    const dismissed = sessionStorage.getItem('paymeo_address_prompt_dismissed') === '1';
    if (needsAddress && !location.pathname.startsWith('/setup/') && !dismissed) {
      setShowAddressPrompt(true);
    } else {
      setShowAddressPrompt(false);
    }
  }, [currentUser, location.pathname]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const handleToggleAccountType = async (nextType) => {
    if (nextType === accountType) return;

    if (nextType === 'personal') {
      setAccountType('personal');
      const result = await switchToPersonal();

      if (!result.success) {
        showToast(result.error, 'error');
        setAccountType('business');
        return;
      }

      setIsDrawerOpen(false);
      const personalUrl = (process.env.REACT_APP_PERSONAL_URL || 'https://paymeo.co').replace(/\/+$/, '');
 
      const tokenResult = await getPersonalToken();
      if (tokenResult.success && tokenResult.ssoToken) {
        showToast(result.message, 'success');
        window.location.href = `${personalUrl}/home?token=${encodeURIComponent(tokenResult.ssoToken)}`;
      } else {
        showToast(tokenResult.error || result.message, tokenResult.success ? 'success' : 'error');
       window.location.href = personalUrl;
      }
    } else {
      setAccountType('business');
    }
  };

  // Check if we're on the chats page for collapsed sidebar
  const isChatsPage = location.pathname === "/chats";
  const isCollapsedSidebar = isChatsPage;

  const pageTitles = {
    "/wallet": "Wallet",
    "/store": "Store",
    "/orders": "Orders",
    "/transactions": "Transactions",
    "/invoices": "Invoices",
    "/meoAi": "Meo AI",
    "/chats": "Flows",
    "/settings": "Settings",
  };

  const mobileHeaderPaddingTop = location.pathname === "/dashboard" ? "pt-[95px]" : "pt-[52px]";

  // Scroll-hide for mobile header
  const handleScroll = useCallback(() => {
    if (window.innerWidth >= 1024) return;
    const mainEl = document.querySelector('main');
    const currentScrollY = mainEl ? mainEl.scrollTop : 0;
    const scrollDirection = currentScrollY > lastScrollY ? 'down' : 'up';

    if (scrollDirection === 'down' && currentScrollY > 60) {
      setHeaderVisible(false);
    } else if (scrollDirection === 'up') {
      setHeaderVisible(true);
    }
    if (currentScrollY < 50) {
      setHeaderVisible(true);
    }

    setLastScrollY(currentScrollY);

    if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    scrollTimeoutRef.current = setTimeout(() => {
      if (window.innerWidth < 1024 && currentScrollY > 60) {
        setHeaderVisible(true);
      }
    }, 2000);
  }, [lastScrollY]);

  useEffect(() => {
    const mainEl = document.querySelector('main');
    if (mainEl) {
      mainEl.addEventListener('scroll', handleScroll, { passive: true });
    }
    return () => {
      if (mainEl) mainEl.removeEventListener('scroll', handleScroll);
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
    };
  }, [handleScroll]);

  // ===================
  // Unified Filled Icon Set - Lucide icons
  // ===================
  const getIconComponent = (iconName, className = "w-6 h-6") => {
    const iconMap = {
      home: <Home className={className} />,
      store: <Store className={className} />,
      orders: <ShoppingBag className={className} />,
      invoices: <FileText className={className} />,
      meo: <Sparkles className={className} />,
      transactions: <ArrowLeftRight className={className} />,
      pricing: <Percent className={className} />,
      wallet: <Wallet className={className} />,
      settings: <Settings className={className} />,
      requests: <Inbox className={className} />,
      flows: <Mail className={className} />,
    };
    return iconMap[iconName] || <Circle className={className} />;
  };

  // ===================
  // Menu Items (unchanged)
  // ===================
  const menuItems = [
    { path: "/dashboard", label: "Home", icon: "home" },
    { path: "/wallet", label: "Wallet", icon: "wallet" },
    { path: "/store", label: "Store", icon: "store" },
    { path: "/orders", label: "Orders", icon: "orders" },
    { path: "/transactions", label: "Transactions", icon: "transactions" },
    { path: "/invoices", label: "Invoices", icon: "invoices" },
    { path: "/settings#meo-voice", label: "Meo AI", icon: "meo" },
    { path: "/chats", label: "Flows", icon: "flows" },
  ];

  return (
    // EXACT same theme wrapper as original - NOTHING changed
    <div className={`${theme === 'dark' || theme === 'dim' ? 'dark' : ''} ${theme === 'dim' ? 'dim' : ''}`}>
      <div className={`flex h-screen bg-white dark:bg-black overflow-hidden max-w-[100vw] transition-colors duration-300`}>
        {/* Sidebar Overlay */}
        <div
          className={`fixed inset-0 bg-black bg-opacity-50 z-40 transition-opacity lg:hidden ${
            sidebarOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
          onClick={() => setSidebarOpen(false)}
        />

        {/* Sidebar - ONLY the look changed to Twitter/personal style */}
        <aside
          className={`fixed top-0 left-0 h-full bg-white dark:bg-black border-r border-gray-200 dark:border-gray-800 z-50 transform transition-all lg:static lg:translate-x-0 transition-colors duration-200 overflow-y-auto overflow-x-hidden
            ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
            ${isCollapsedSidebar ? "w-[88px]" : "w-[88px] xl:w-[275px]"}`}
        >
          {/* Logo */}
          <div className={`p-4 mb-2 flex ${isCollapsedSidebar ? 'justify-center' : 'justify-center xl:justify-start'}`}>
            <Link to="/dashboard">
              <img src="/assets/favicon.png" alt="Logo" className="w-8 h-8" />
            </Link>
          </div>

          {/* Navigation Menu */}
          <nav className={`flex-1 flex flex-col items-center xl:items-stretch px-2 space-y-1`}>
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center rounded-full transition-all text-sm ${
                    isActive
                      ? "font-bold text-black dark:text-white"
                      : "text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-900 hover:text-black dark:hover:text-white"
                  } ${isCollapsedSidebar ? 'justify-center p-3' : 'justify-center xl:justify-start gap-4 p-3'}`}
                  onClick={() => setSidebarOpen(false)}
                  title={isCollapsedSidebar ? item.label : ''}
                >
                  <div className="w-7 h-7 flex items-center justify-center">
                    {getIconComponent(item.icon)}
                  </div>
                  {!isCollapsedSidebar && (
                    <span className="hidden xl:inline text-xl">{item.label}</span>
                  )}
                </Link>
              );
            })}

            {/* More button — opens SideDrawer */}
            <button
              onClick={() => { setSidebarOpen(false); setIsDrawerOpen(true); }}
              className={`flex items-center rounded-full transition-all text-sm text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-900 hover:text-black dark:hover:text-white ${
                isCollapsedSidebar ? 'justify-center p-3' : 'justify-center xl:justify-start gap-4 p-3'
              }`}
            >
              <div className="w-7 h-7 flex items-center justify-center">
                <MoreHorizontal className="w-7 h-7" />
              </div>
              {!isCollapsedSidebar && (
                <span className="hidden xl:inline text-xl">More</span>
              )}
            </button>

            {/* Create Button */}
            <div className="mt-6 w-full flex justify-center xl:justify-start px-3">
              <button
                className={`w-12 h-12 ${isCollapsedSidebar ? '' : 'xl:w-full'} xl:h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-full shadow-lg flex items-center justify-center ${isCollapsedSidebar ? '' : 'xl:px-4'} transition-colors font-bold text-sm`}
                onClick={() => navigate('/store#create')}
              >
                <Plus className={`w-5 h-5 ${isCollapsedSidebar ? '' : 'xl:hidden'}`} />
                {!isCollapsedSidebar && <span className="hidden xl:inline">Add Item</span>}
              </button>
            </div>
          </nav>
        </aside>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-h-0">
          {/* Mobile header wrapper with scroll-hide */}
          <div
            className="lg:hidden z-30"
            style={{
              transform: headerVisible ? 'translateY(0)' : 'translateY(-100%)',
              transition: 'transform 0.3s ease',
              position: 'fixed',
              top: 0, left: 0, right: 0,
            }}
          >
            <div className="backdrop-blur-xl bg-white/80 dark:bg-black/80 border-b border-gray-200/70 dark:border-gray-800/70">
              {/* Top row: avatar, logo/page title, icons */}
              <div className="flex items-center h-[52px] px-4 relative">
                {/* Left: avatar */}
                <div className="w-10 flex justify-start z-20">
                  <button
                    onClick={() => setIsDrawerOpen(true)}
                    className="active:scale-95 transition-transform shrink-0"
                  >
                    <img
                      src={currentUser?.company_logo || "/assets/default-avatar.png"}
                      alt="Profile"
                      className="w-7 h-7 rounded-full object-cover"
                    />
                  </button>
                </div>

                {/* Center: logo on home, page name elsewhere */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  {location.pathname === "/dashboard" ? (
                    <Link to="/dashboard" className="pointer-events-auto">
                      <img src="/assets/favicon.png" alt="Logo" className="h-7 w-auto" />
                    </Link>
                  ) : (
                    <span className="pointer-events-auto text-sm font-bold text-black dark:text-white">
                      {pageTitles[location.pathname] || ""}
                    </span>
                  )}
                </div>

                {/* Right: icons */}
                <div className="flex-1 flex justify-end items-center gap-0 z-20">
                  <Link
                    to="/wallet"
                    className="w-10 h-10 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-full transition-colors active:bg-gray-200 dark:active:bg-gray-700"
                  >
                    <Wallet size={22} />
                  </Link>
                  <Link
                    to="/store#orders"
                    className="w-10 h-10 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-800 rounded-full transition-colors active:bg-gray-200 dark:active:bg-gray-700"
                  >
                    <ClipboardList size={22} />
                  </Link>
                </div>
              </div>

              {/* For You | Shop Asks tabs - hidden temporarily, showing only Shop Asks */}
            </div>
          </div>

          {/* Page Content */}
          <main className={`flex-1 min-h-0 bg-white dark:bg-black transition-colors duration-300 ${mobileHeaderPaddingTop} lg:pt-0 overflow-y-auto overflow-x-hidden`}>
            <DashboardTabContext.Provider value={{ dashboardTab, setDashboardTab }}>
              {children}
            </DashboardTabContext.Provider>
          </main>
        </div>

        {/* Mobile Nav */}
        <MobileBottomNav />

        {/* SideDrawer */}
        <SideDrawer
          isOpen={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          theme={theme}
          toggleTheme={toggleTheme}
          currentUser={currentUser}
          onLogout={handleLogout}
          accountType={accountType}
          onToggleAccountType={handleToggleAccountType}
        />

        {/* Login-time address prompt for sellers without a submitted address */}
        <AddressPromptModal
          show={showAddressPrompt}
          onClose={() => setShowAddressPrompt(false)}
        />
      </div>
    </div>
  );
};

export default Layout;